/**
 * tab-identity.ts — an open tab follows the person signed in to this browser (Q13 part 4,
 * quality session 30 October 2026).
 *
 * ⭐ THE PROBLEM. Every tab on app.nexanomics.com shares one cookie jar, so a sign-in or sign-out
 * in one tab changes the session underneath every other tab — but the other tabs go on showing
 * the screens they loaded, for the person they loaded them for. Within one zone that is worse
 * than stale: the other tab's next API call carries the NEW person's token while it shows the
 * OLD person's data. Across zones the old tab's calls simply fail. Neither may happen.
 *
 * ⭐⭐ THE MECHANISM. A sign-in or sign-out announces the browser's identity — the signed-in
 * person's Cognito `sub`, or '' for nobody — under one localStorage key. Every tab knows whom IT
 * is showing (set by `useAuth` from its own session). When the two differ, the tab reloads: into
 * the new person (their zone is in `nexa.zone`, their tokens in the cookies) or, if nobody is
 * signed in, onto the sign-in page. It acts:
 *   - the moment the announcement lands, if the tab is visible (the `storage` event);
 *   - when the tab is next shown or focused, if it was hidden;
 *   - before any API call, as a last line — a call that would go out as somebody else is refused.
 *
 * ⚠ ONLY AN ANNOUNCEMENT CAN TRIGGER IT. A tab never compares itself with cookies on its own: a
 * browser still holding sessions from before this existed (one per zone) would otherwise make
 * two tabs reload each other forever. No announcement, no action.
 *
 * ⚠ AND IT CAN NEVER LOOP. A tab follows any one announcement once; if it is still out of step
 * after that, it goes to the sign-in page instead of reloading again.
 */

/** The browser's announced identity: a Cognito `sub`, or '' when nobody is signed in. */
export const IDENTITY_KEY = 'nexa.identity';
/** The announcement this tab last followed (per tab), so it can never reload twice for one. */
const FOLLOWED_KEY = 'nexa.identity.followed';

/** Whom this tab is showing: a `sub`, `null` (nobody), or `undefined` (not yet known). */
let tabIdentity: string | null | undefined;
let installed = false;

/** Set by `useAuth` from this tab's own session, every time it is checked. */
export function setTabIdentity(sub: string | null): void {
  tabIdentity = sub;
  // In step with the browser again: a later announcement of this same person (they sign out and
  // back in) must be followed normally, not treated as one this tab already failed to follow.
  if (sub !== null && announced() === sub) {
    try {
      window.sessionStorage.removeItem(FOLLOWED_KEY);
    } catch {
      /* private mode */
    }
  }
}

/** Whom this tab is showing — exported for tests. */
export const currentTabIdentity = (): string | null | undefined => tabIdentity;

function announced(): string | null {
  try {
    return window.localStorage.getItem(IDENTITY_KEY);
  } catch {
    return null;
  }
}

/**
 * Tell every tab who is now signed in to this browser (`null` = nobody). Called by a sign-in
 * that completes and by a person signing out — never by Amplify's own internal sign-outs.
 */
export function announceIdentity(sub: string | null): void {
  tabIdentity = sub;
  try {
    window.localStorage.setItem(IDENTITY_KEY, sub ?? '');
  } catch {
    /* private mode — other tabs fall back to failing their next call, as before */
  }
}

/** Is this tab showing someone other than the person now signed in to the browser? */
export function identityChanged(): boolean {
  const now = announced();
  return typeof tabIdentity === 'string' && now !== null && now !== tabIdentity;
}

/**
 * Leave the old person's screens: reload into the new identity, or go to sign-in. `seen` is the
 * person a refused API token belonged to, used when nothing was announced (a tab of an older
 * bundle signed them in).
 */
export function followIdentityChange(seen?: string): void {
  const now = announced() ?? seen ?? '';
  let followed: string | null = null;
  try {
    followed = window.sessionStorage.getItem(FOLLOWED_KEY);
    window.sessionStorage.setItem(FOLLOWED_KEY, now);
  } catch {
    /* private mode */
  }
  tabIdentity = undefined; // no further action until this tab knows itself again
  if (now === '' || followed === now) {
    window.location.assign('/sign-in');
  } else {
    window.location.reload();
  }
}

/** Act on a changed identity now, if there is one. Returns true when the tab is leaving. */
export function checkTabIdentity(): boolean {
  if (!identityChanged()) return false;
  followIdentityChange();
  return true;
}

/**
 * The last line, run before every authenticated API call: is this call about to go out as
 * somebody other than the person this tab is showing? `tokenSub` is the `sub` of the token the
 * call would carry. Returns false (and leaves the tab) when it must not be sent.
 */
export function tokenMatchesTab(tokenSub: string | undefined): boolean {
  if (checkTabIdentity()) return false;
  if (typeof tabIdentity === 'string' && tokenSub && tokenSub !== tabIdentity) {
    followIdentityChange(tokenSub);
    return false;
  }
  return true;
}

/** Install the listeners, once, at boot. */
export function installTabIdentityGuard(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('storage', (e) => {
    if (e.key !== IDENTITY_KEY) return;
    if (document.visibilityState === 'visible') checkTabIdentity();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkTabIdentity();
  });
  window.addEventListener('focus', () => { checkTabIdentity(); });
  window.addEventListener('pageshow', () => { checkTabIdentity(); });
}

/** Test seam. */
export function __resetTabIdentity(): void {
  tabIdentity = undefined;
}
